import type { FC, ReactNode } from "react";
import { memo, useEffect, useMemo } from "react";
import { useCart } from "@/hooks/cart/cart";
import { usePromocode } from "@/hooks/promocode";
import { useShippings } from "@/hooks/shippings/shippings";
import { Panel, PanelHeading } from "@/styles/utils/StyledPanel";
import { Hr, RowOffset } from "@/styles/utils/Utils";
import { colors } from "@/styles/utils/vars";
import { ButtonGroup, cssButtonGroupVertical } from "@/ui/Button/StyledButton";
import { Typography } from "@/ui/Typography/Typography";
import { CURRENCY } from "@/utils/constants";
import { convertPennyToRub, dateToString, isCurrentYear } from "@/utils/common/helpers";
import { StyledSummary, StyledSummaryItem } from "./StyledSummary";
import { SummaryItem } from "./SummaryItem";
import { cssListVertical, StyledList } from "../../List/StyledList";
import { Price } from "../../Products/parts";
export const Summary: FC<{
  summaryExtends?: ReactNode;
}> = memo(({
  children,
  summaryExtends
}) => {
  const {
    totalCost,
    token,
    productCost,
    shipping: {
      nextDate: nextShippingDate
    }
  } = useCart();
  const {
    shippingCost,
    shippingType,
    updateShippingCost
  } = useShippings();
  const {
    discount,
    promocode
  } = usePromocode({
    cart: token
  });
  const nextShippingDateText = useMemo(() => !!nextShippingDate.date ? dateToString(nextShippingDate.date, !isCurrentYear(nextShippingDate.date)) : "", [nextShippingDate.date]);
  const shippingCostText = useMemo(() => {
    if (shippingType === "company") {
      return "По тарифам ТК";
    }
    return shippingCost !== null ? shippingCost > 0 ? `${convertPennyToRub(shippingCost)} ${CURRENCY}` : "бесплатно" : "";
  }, [shippingCost, shippingType]);
  useEffect(() => {
    return function cleanup() {
      updateShippingCost(null);
    };
  }, [updateShippingCost]);
  return <StyledSummary>
      <Panel>
        <PanelHeading>Ваш заказ</PanelHeading>
        <StyledList className={cssListVertical}>
          {nextShippingDate.date !== null && <SummaryItem color={colors.grayDark}>
              <Typography variant={"p12"}>
                Ближайшая дата доставки: {nextShippingDateText}
              </Typography>
            </SummaryItem>}
          {productCost > 0 && <SummaryItem title={"Товары"} value={<Price price={productCost} isClean />} />}
          {promocode !== null && <SummaryItem title={`Экономия с промокодом ${promocode}`} value={<Price price={discount} isClean isSale />} type={"red"} />}
          {shippingCost !== null && <SummaryItem title={`Доставка`} value={shippingCostText} />}
          {summaryExtends}
        </StyledList>
        <Hr />
        <RowOffset>
          <StyledSummaryItem as={"div"}>
            <Typography>Итого</Typography>
            <Price price={totalCost} variant={"total"} isClean />
          </StyledSummaryItem>
        </RowOffset>

        <ButtonGroup className={cssButtonGroupVertical}>{children}</ButtonGroup>
      </Panel>
    </StyledSummary>;
});
Summary.displayName = "Summary";